import { Controller } from "@hotwired/stimulus"
import { NativeBridge } from "../lib/native_bridge"
import { pageCovered, whenTabShown } from "../lib/overlay"

// BusinessConstants::REVIEW_* (Ruby) — keep in sync.
const REVIEW_CONFIG = {
  BOOKMARK_THRESHOLD: 3,
  PRODUCT_CREATED_DELAY_MS: 3000,
  // After a failed bridge call (old build, no Play Store…): don't retry on every later bookmark.
  RETRY_COOLDOWN_MS: 3_600_000,
  // Wait for the publish celebration / any open dialog to finish, up to this long.
  SETTLE_POLL_MS: 500,
  SETTLE_MAX_MS: 15_000,
  CREATION_EVENTS: { product_created: "product", listing_created: "listing" },
  STORAGE_KEYS: {
    BOOKMARK_COUNT: "toas2t_review_bookmark_count",
    REQUESTED_PREFIX: "toas2t_review_requested_",
    LAST_FAILED_AT: "toas2t_review_last_failed_at",
    MIGRATED_PREFIX: "toas2t_review_migrated_u"
  }
}

function read(key) {
  try { return localStorage.getItem(key) } catch { return null }
}

function write(key, value) {
  try { localStorage.setItem(key, value) } catch { /* private mode: asks again later */ }
}

// In-app review (App Store / Google Play) at two engagement points, native shells only:
// - the owner's landing right after a wizard published a product or listing (server flash[:review_prompt], auto-trigger),
//   asked once the publish celebration and any dialog are gone and the tab is on screen;
// - the Nth successful bookmark (turbo:submit-end bubbling to the parent <nav>, which survives the button's stream swap).
// Each reason is asked once per user on this device. Neither platform reports whether the dialog showed.
export default class extends Controller {
  static values = {
    event: String, // "bookmark" | "product_created" | "listing_created"
    threshold: { type: Number, default: REVIEW_CONFIG.BOOKMARK_THRESHOLD },
    autoTrigger: { type: Boolean, default: false },
    userId: { type: Number, default: 0 }
  }

  connect() {
    if (!NativeBridge.isNative()) return
    this._migrateGlobalKeys()

    const reason = REVIEW_CONFIG.CREATION_EVENTS[this.eventValue]
    if (this.autoTriggerValue && reason && !this._hasRequestedFor(reason)) {
      this._creationTimer = setTimeout(() => this._whenSettled(() => this._requestReview(reason)), REVIEW_CONFIG.PRODUCT_CREATED_DELAY_MS)
    }
  }

  disconnect() {
    clearTimeout(this._creationTimer)
    clearTimeout(this._settleTimer)
    this._cancelTabWait?.()
  }

  // turbo:submit-end from a bookmark form inside this element.
  handleBookmarkFromParent(event) {
    const form = event.target
    if (!form?.action?.includes("/bookmarks") || !event.detail?.success || !NativeBridge.isNative()) return
    this.handleBookmark(event)
  }

  handleBookmark(event) {
    if (event.target?.querySelector('[name="_method"]')?.value === "delete") return
    if (this._incrementBookmarkCount() >= this.thresholdValue) this._requestReview("bookmark")
  }

  // Motion cue gone (it removes itself), nothing covering the page, this tab on screen — or give up waiting.
  _whenSettled(callback, waited = 0) {
    const busy = document.querySelector('#motion-cue [data-controller~="motion-cue"]') || pageCovered()
    if (busy && waited < REVIEW_CONFIG.SETTLE_MAX_MS) {
      this._settleTimer = setTimeout(() => this._whenSettled(callback, waited + REVIEW_CONFIG.SETTLE_POLL_MS), REVIEW_CONFIG.SETTLE_POLL_MS)
      return
    }
    if (busy) return
    this._cancelTabWait = whenTabShown(callback)
  }

  async _requestReview(reason) {
    if (this._hasRequestedFor(reason) || this._isInCooldown()) return
    try {
      await NativeBridge.Review.request()
      this._markRequested(reason)
      this._clearCooldown()
      this.dispatch("requested", { detail: { reason } })
    } catch {
      this._markCooldown()
    }
  }

  _isInCooldown() {
    const failedAt = parseInt(read(this._storageKey(REVIEW_CONFIG.STORAGE_KEYS.LAST_FAILED_AT)))
    return Boolean(failedAt) && Date.now() - failedAt < REVIEW_CONFIG.RETRY_COOLDOWN_MS
  }

  _markCooldown() {
    write(this._storageKey(REVIEW_CONFIG.STORAGE_KEYS.LAST_FAILED_AT), Date.now().toString())
  }

  _clearCooldown() {
    try { localStorage.removeItem(this._storageKey(REVIEW_CONFIG.STORAGE_KEYS.LAST_FAILED_AT)) } catch { /* ignore */ }
  }

  // Per-user keys: a shared device doesn't carry one user's counts over to another.
  _storageKey(baseKey) {
    return this.userIdValue > 0 ? `${baseKey}_u${this.userIdValue}` : baseKey
  }

  // One-time copy of the pre-per-user global keys into this user's keys.
  _migrateGlobalKeys() {
    if (!(this.userIdValue > 0)) return
    const migratedKey = `${REVIEW_CONFIG.STORAGE_KEYS.MIGRATED_PREFIX}${this.userIdValue}`
    if (read(migratedKey)) return

    const globalKeys = [
      REVIEW_CONFIG.STORAGE_KEYS.BOOKMARK_COUNT,
      ...[ "bookmark", "product", "listing" ].map((reason) => `${REVIEW_CONFIG.STORAGE_KEYS.REQUESTED_PREFIX}${reason}`)
    ]
    globalKeys.forEach((globalKey) => {
      const value = read(globalKey)
      if (value && !read(this._storageKey(globalKey))) write(this._storageKey(globalKey), value)
    })
    write(migratedKey, "true")
  }

  _incrementBookmarkCount() {
    const key = this._storageKey(REVIEW_CONFIG.STORAGE_KEYS.BOOKMARK_COUNT)
    const count = (parseInt(read(key)) || 0) + 1
    write(key, count.toString())
    return count
  }

  _hasRequestedFor(reason) {
    return read(this._storageKey(`${REVIEW_CONFIG.STORAGE_KEYS.REQUESTED_PREFIX}${reason}`)) === "true"
  }

  _markRequested(reason) {
    write(this._storageKey(`${REVIEW_CONFIG.STORAGE_KEYS.REQUESTED_PREFIX}${reason}`), "true")
  }
}
